import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
    type UIEventHandler,
} from "react";

import "../../theme/css/CustomScrollbar.css";

export type CustomScrollbarHandle = HTMLDivElement;

export interface CustomScrollbarProps {
    children?: ReactNode;
    direction?: "vertical" | "horizontal" | "both";
    height?: string | number;
    minHeight?: string | number;
    maxHeight?: string | number;
    noPadding?: boolean;
    disableScroll?: boolean;
    autoHide?: boolean;
    autoHideDelay?: number;
    className?: string;
    style?: CSSProperties;
    onScroll?: UIEventHandler<HTMLDivElement>;
}

const CustomScrollbar = forwardRef<
    CustomScrollbarHandle,
    CustomScrollbarProps
>(
    (
        {
            children,
            direction = "vertical",
            height,
            minHeight,
            maxHeight,
            noPadding = false,
            disableScroll = false,
            autoHide = true,
            autoHideDelay = 700,
            className = "",
            style,
            onScroll,
        },
        ref
    ) => {
        const scrollRef = useRef<HTMLDivElement>(null);

        const hideTimerRef = useRef<number | null>(null);

        const [isScrolling, setIsScrolling] =
            useState(false);

        const [verticalThumb, setVerticalThumb] =
            useState({
                top: 0,
                height: 40,
                visible: false,
            });

        const [horizontalThumb, setHorizontalThumb] =
            useState({
                left: 0,
                width: 40,
                visible: false,
            });

        useImperativeHandle(
            ref,
            () => scrollRef.current as HTMLDivElement
        );

        // =============================================
        // Calculate custom thumb positions
        // =============================================

        const updateThumbs = useCallback(() => {
            const element = scrollRef.current;

            if (!element) return;

            const {
                scrollTop,
                scrollLeft,
                scrollHeight,
                scrollWidth,
                clientHeight,
                clientWidth,
            } = element;

            // -----------------------------------------
            // Vertical
            // -----------------------------------------

            const hasVerticalScroll =
                scrollHeight > clientHeight;

            if (hasVerticalScroll) {
                const minThumbHeight = 40;

                const calculatedHeight =
                    (clientHeight / scrollHeight) *
                    clientHeight;

                const thumbHeight = Math.max(
                    calculatedHeight,
                    minThumbHeight
                );

                const maxScroll =
                    scrollHeight - clientHeight;

                const maxThumbTop =
                    clientHeight - thumbHeight;

                const top =
                    maxScroll > 0
                        ? (scrollTop / maxScroll) *
                          maxThumbTop
                        : 0;

                setVerticalThumb({
                    top,
                    height: thumbHeight,
                    visible: true,
                });
            } else {
                setVerticalThumb((current) => ({
                    ...current,
                    visible: false,
                }));
            }

            // -----------------------------------------
            // Horizontal
            // -----------------------------------------

            const hasHorizontalScroll =
                scrollWidth > clientWidth;

            if (hasHorizontalScroll) {
                const minThumbWidth = 40;

                const calculatedWidth =
                    (clientWidth / scrollWidth) *
                    clientWidth;

                const thumbWidth = Math.max(
                    calculatedWidth,
                    minThumbWidth
                );

                const maxScroll =
                    scrollWidth - clientWidth;

                const maxThumbLeft =
                    clientWidth - thumbWidth;

                const left =
                    maxScroll > 0
                        ? (scrollLeft / maxScroll) *
                          maxThumbLeft
                        : 0;

                setHorizontalThumb({
                    left,
                    width: thumbWidth,
                    visible: true,
                });
            } else {
                setHorizontalThumb((current) => ({
                    ...current,
                    visible: false,
                }));
            }
        }, []);

        // =============================================
        // Scroll
        // =============================================

        const handleScroll: UIEventHandler<HTMLDivElement> =
            useCallback(
                (event) => {
                    updateThumbs();

                    setIsScrolling(true);

                    if (hideTimerRef.current !== null) {
                        window.clearTimeout(
                            hideTimerRef.current
                        );
                    }

                    if (autoHide) {
                        hideTimerRef.current =
                            window.setTimeout(() => {
                                setIsScrolling(false);
                            }, autoHideDelay);
                    }

                    onScroll?.(event);
                },
                [
                    autoHide,
                    autoHideDelay,
                    onScroll,
                    updateThumbs,
                ]
            );

        // =============================================
        // Initial calculation / resize
        // =============================================

        useEffect(() => {
            updateThumbs();

            const element = scrollRef.current;

            if (!element) return;

            const observer = new ResizeObserver(() => {
                updateThumbs();
            });

            observer.observe(element);

            return () => {
                observer.disconnect();
            };
        }, [updateThumbs, children]);

        // =============================================
        // Cleanup
        // =============================================

        useEffect(() => {
            return () => {
                if (hideTimerRef.current !== null) {
                    window.clearTimeout(
                        hideTimerRef.current
                    );
                }
            };
        }, []);

        const classes = [
            "custom-scrollbar",

            `custom-scrollbar--${direction}`,

            disableScroll
                ? "custom-scrollbar--disabled"
                : "",

            className,
        ]
            .filter(Boolean)
            .join(" ");

        const containerStyle: CSSProperties = {
            width: "100%",
            height,
            minHeight,
            maxHeight,
            ...style,
        };

        const showThumb =
            !autoHide || isScrolling;

        return (
            <div
                className="custom-scrollbar-wrapper"
                style={containerStyle}
            >
                <div
                    ref={scrollRef}
                    className={classes}
                    onScroll={handleScroll}
                    style={{
                        paddingBottom:
                            noPadding ? 0 : 14,
                    }}
                >
                    {children}
                </div>

                {/* Vertical custom thumb */}

                {direction !== "horizontal" &&
                    verticalThumb.visible && (
                        <div
                            className="custom-scrollbar-thumb custom-scrollbar-thumb--vertical"
                            style={{
                                top: verticalThumb.top,
                                height:
                                    verticalThumb.height,
                                opacity: showThumb
                                    ? 1
                                    : 0,
                            }}
                        />
                    )}

                {/* Horizontal custom thumb */}

                {direction !== "vertical" &&
                    horizontalThumb.visible && (
                        <div
                            className="custom-scrollbar-thumb custom-scrollbar-thumb--horizontal"
                            style={{
                                left:
                                    horizontalThumb.left,
                                width:
                                    horizontalThumb.width,
                                opacity: showThumb
                                    ? 1
                                    : 0,
                            }}
                        />
                    )}
            </div>
        );
    }
);

CustomScrollbar.displayName = "CustomScrollbar";

export default CustomScrollbar;
